import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

export type AnimatedNumberProps = {
  /** Current target value. Component tweens from the previously-rendered value to this. */
  value: number;
  /** Optional formatter applied at each frame -- e.g. (n) => formatDurationCompact(n). */
  format?: (n: number) => string;
  /** Animation duration, ms. Default 480. */
  durationMs?: number;
  /** Decimals when no formatter is provided. Default 0. */
  decimals?: number;
  /** Whether negative -> positive transitions should round-trip via 0. Rarely needed. */
  preventNegative?: boolean;
  className?: string;
};

/**
 * Tweens a number from the previously-rendered value to `value` over
 * ~480ms using an ease-out cubic. Used wherever the dashboard surfaces
 * a live stat (this-week minutes, streak day count, quiz score). Per the
 * plan: "Dashboard numbers tween from old to new value when invalidated"
 * -- this is that primitive.
 *
 * Respects prefers-reduced-motion -- in that mode the number swaps
 * instantly without animation.
 */
export function AnimatedNumber({
  value,
  format,
  durationMs = 480,
  decimals = 0,
  preventNegative,
  className,
}: AnimatedNumberProps) {
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);

  useEffect(() => {
    if (reduce) {
      fromRef.current = value;
      setDisplay(value);
      return;
    }
    const from = fromRef.current;
    if (from === value) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = performance.now();
    const target = preventNegative ? Math.max(0, value) : value;

    const step = (now: number) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const next = from + (target - from) * eased;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = target;
        setDisplay(target);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, durationMs, reduce, preventNegative]);

  const out = format
    ? format(display)
    : display.toFixed(decimals);

  return <span className={className}>{out}</span>;
}
